import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../App';
import Navigation from '../components/Navigation';
import { CheckCircle, Loader } from 'lucide-react';

const HerdSuccess = ({ user, logout }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [message, setMessage] = useState('Verifying your payment...');
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setStatus('error');
      setMessage('No payment session found');
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, currentAttempt = 0) => {
    const maxAttempts = 5;
    
    if (currentAttempt >= maxAttempts) {
      setStatus('error');
      setMessage('Payment verification timed out. Please check your email for confirmation or contact support.');
      return;
    }

    try {
      const response = await api.get(`/subscriptions/checkout-status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        setMessage('Payment successful! Your Herd subscription has been activated.');
        setTimeout(() => {
          navigate('/the-herd');
        }, 3000);
      } else if (response.data.status === 'expired') {
        setStatus('error');
        setMessage('Payment session expired. Please try again.');
      } else {
        // Payment still processing, continue polling
        setAttempts(currentAttempt + 1);
        setTimeout(() => {
          pollPaymentStatus(sessionId, currentAttempt + 1);
        }, 2000);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      if (currentAttempt < maxAttempts - 1) {
        setAttempts(currentAttempt + 1);
        setTimeout(() => {
          pollPaymentStatus(sessionId, currentAttempt + 1);
        }, 2000);
      } else {
        setStatus('error');
        setMessage('Unable to verify payment. Please contact support if you were charged.');
      }
    }
  };

  return (
    <div className="min-h-screen" style={{backgroundColor: '#FCFCF8'}}>
      <Navigation user={user} logout={logout} />
      
      <div className="max-w-md mx-auto px-4 py-16 text-center">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {status === 'checking' && (
            <>
              <Loader className="w-16 h-16 mx-auto mb-4 animate-spin" style={{color: '#D66238'}} />
              <h1 className="text-2xl font-bold mb-2" style={{color: '#D66238'}}>
                Processing Payment
              </h1>
              <p className="text-gray-600">{message}</p>
              {attempts > 0 && (
                <p className="text-sm text-gray-500 mt-2">Attempt {attempts + 1} of 5...</p>
              )}
            </>
          )}
          
          {status === 'success' && (
            <>
              <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-500" />
              <h1 className="text-2xl font-bold mb-2 text-green-600">
                Payment Successful!
              </h1>
              <p className="text-gray-600 mb-4">{message}</p>
              <p className="text-sm text-gray-500">Redirecting to The Herd...</p>
            </>
          )}
          
          {status === 'error' && (
            <>
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
                <span className="text-3xl">⚠️</span>
              </div>
              <h1 className="text-2xl font-bold mb-2 text-red-600">
                Payment Issue
              </h1>
              <p className="text-gray-600 mb-4">{message}</p>
              <button
                onClick={() => navigate('/the-herd')}
                className="px-6 py-2 rounded-lg text-white"
                style={{backgroundColor: '#D66238'}}
              >
                Go to The Herd
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default HerdSuccess;
